import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useErrorBoundary } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	GraphQLErrorMessage,
	Heading,
	Heading3,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

function GoalLabelEditor(props: { goalLabel?: graphql.Label }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateLabelMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateLabelMutation();

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: props.goalLabel?.title ?? [],
			description: props.goalLabel?.description ?? [],
		},
		onSubmit: () => onUpdate(),
	});

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			description: formik.values.description?.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
		};

		if (props.goalLabel) {
			await update({
				variables: {
					input: {
						id: props.goalLabel.id,
						type: graphql.LabelType.Goal,
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: {
					input: {
						type: graphql.LabelType.Goal,
						...input,
					},
				},
			});

			if (data) {
				history.push(`/settings/goals/labels/${data.createLabel.label.id}`);
			}
		}

		addToast(t('common.settings.goals.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const valid = formik.values.title.some((translation) => translation.text.length > 0);

	return (
		<Flex flexDirection="column">
			<Flex flexDirection="row" alignItems="center">
				<Heading>
					{t(`common.settings.goals.label.${props.goalLabel ? 'edit' : 'create'}.title`)}
				</Heading>
			</Flex>
			<Heading3 mt={`${tokens.gap.section}px`} mb={`${tokens.gap.group}px`}>
				{t('common.settings.goals.edit.details')}
			</Heading3>
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.goals.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.goals.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.Multilingual,
							},
						]}
					/>
				</Form>
			</FormikProvider>

			<Flex justifyContent="flex-end">
				<Button
					title={t('common.save')}
					onClick={() => formik.submitForm()}
					loading={loading}
					disabled={!valid}
				/>
			</Flex>

			<Flex mt={4}>
				{props.goalLabel && (
					<ActionArchiveListItem
						archivableItem={props.goalLabel}
						archivableItemName={t('common.settings.goals.label.itemName')}
						archivableItemKey="label"
						useArchive={graphql.useArchiveLabelMutation}
						useRestore={graphql.useRestoreLabelMutation}
					/>
				)}
			</Flex>
		</Flex>
	);
}

function GoalLabelEditorID(props: { id: string }) {
	const { data, error, loading } = graphql.useLabelQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return (
			<Flex my={4} justifyContent="center">
				<Spinner />
			</Flex>
		);
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <GoalLabelEditor goalLabel={data!.label as graphql.Label} />;
	}
}

export { GoalLabelEditor as GoalLabelCreator };

export default GoalLabelEditorID;
